import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { moodsApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { TrendingUp, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const MOOD_COLORS = {
  1: '#ef4444',
  2: '#f97316',
  3: '#eab308',
  4: '#22c55e',
  5: '#00d4ff',
};

const MoodDot = ({ level, size = 8 }) => (
  <span
    className="inline-block rounded-full"
    style={{ width: size, height: size, backgroundColor: MOOD_COLORS[level] || '#475569' }}
  />
);

export default function MoodTrendWidget() {
  const { t } = useLanguage();
  const [trend, setTrend] = useState([]);
  const [avg, setAvg] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await moodsApi.getAnalytics();
        const raw = res.data.trend || [];
        setAvg(res.data.average_mood || 0);

        const grouped = {};
        for (const entry of raw) {
          if (!grouped[entry.date]) grouped[entry.date] = [];
          grouped[entry.date].push(entry.level);
        }

        const today = new Date();
        const days = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(d.getDate() - i);
          const key = d.toISOString().slice(0, 10);
          const levels = grouped[key] || [];
          const avgLevel = levels.length > 0 ? levels.reduce((a, b) => a + b, 0) / levels.length : null;
          days.push({
            date: key,
            day: d.toLocaleDateString(undefined, { weekday: 'short' }),
            level: avgLevel ? Math.round(avgLevel) : null,
            avgLevel,
          });
        }
        setTrend(days);
      } catch (err) {
        console.error('Mood trend fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return null;

  const hasData = trend.some(d => d.level !== null);

  // SVG chart dimensions
  const W = 280;
  const H = 60;
  const padX = 10;
  const padY = 6;
  const stepX = (W - padX * 2) / 6;

  const points = trend.map((d, i) => {
    if (d.avgLevel === null) return null;
    const x = padX + i * stepX;
    const y = padY + (H - padY * 2) * (1 - (d.avgLevel - 1) / 4);
    return { x, y, level: d.level };
  });

  const validPoints = points.filter(Boolean);

  const linePath = validPoints.length > 1
    ? validPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
    : '';

  const areaPath = linePath && validPoints.length > 1
    ? `${linePath} L ${validPoints[validPoints.length - 1].x} ${H - padY} L ${validPoints[0].x} ${H - padY} Z`
    : '';

  return (
    <Card className="bg-navy-900/40 border-white/5 overflow-hidden" data-testid="mood-trend-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="text-cyan" size={18} />
            <span className="text-white font-medium text-sm">{t('mood_trend_title')}</span>
          </div>
          <Link to="/dashboard/mood" className="flex items-center gap-1 text-xs text-cyan hover:text-cyan/80 transition-colors">
            {t('dashboard_view_all')} <ArrowRight size={12} />
          </Link>
        </div>

        {!hasData ? (
          <p className="text-slate-500 text-xs text-center py-4">{t('mood_trend_empty')}</p>
        ) : (
          <div className="space-y-3">
            {/* Mini line chart */}
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16" preserveAspectRatio="none">
              <defs>
                <linearGradient id="moodGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Grid lines */}
              {[1, 2, 3, 4, 5].map(l => {
                const y = padY + (H - padY * 2) * (1 - (l - 1) / 4);
                return <line key={l} x1={padX} y1={y} x2={W - padX} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />;
              })}
              {/* Area fill */}
              {areaPath && <path d={areaPath} fill="url(#moodGrad)" />}
              {/* Line */}
              {linePath && <path d={linePath} fill="none" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
              {/* Dots */}
              {validPoints.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="3.5" fill={MOOD_COLORS[p.level]} stroke="#0a1929" strokeWidth="1.5" />
              ))}
            </svg>

            {/* Day labels */}
            <div className="flex justify-between px-1">
              {trend.map((d, i) => (
                <div key={i} className="flex flex-col items-center gap-1" style={{ width: `${100 / 7}%` }}>
                  <span className="text-[10px] text-slate-500">{d.day}</span>
                  {d.level !== null ? (
                    <MoodDot level={d.level} size={6} />
                  ) : (
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-700" />
                  )}
                </div>
              ))}
            </div>

            {/* Average badge */}
            {avg > 0 && (
              <div className="flex items-center justify-between pt-1 border-t border-white/5">
                <span className="text-slate-500 text-xs">{t('mood_avg')}</span>
                <div className="flex items-center gap-1.5">
                  <TrendingUp size={12} className="text-cyan" />
                  <span className="text-white text-sm font-heading">{avg}/5</span>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
